import React, { useMemo } from 'react';
import { parseTimeFrame } from '@/utils/dater';
import { useRouter } from 'expo-router';
import ResourceCard from '../General/ResourceCard';
import Avatar from '../People/Avatar';
import { StyleSheet } from 'react-native-unistyles';

/**
 * Bible Study card — same ResourceCard composition as SmallGroupCard with
 * Study/Teacher/Student terminology and /biblestudies routes.
 * Rows come from get_bible_studies (get_small_groups shape + closure fields).
 */
export default function BibleStudyCard({ study, variant = 'hero', heroImage, avatar }) {
    const router = useRouter();

    const teacherAvatar = useMemo(() => {
        if (avatar) return avatar;
        if (!study?.Leader) return null;
        return <Avatar disableBorder size={variant === 'compact' ? 36 : 48} profile={study.Leader} />;
    }, [avatar, study?.Leader, variant]);

    const statusConfig = useMemo(() => {
        const configs = {
            ACTIVE: { color: "success", label: "Active" },
            COMPLETED: { color: "info", label: "Completed" },
            CANCELED: { color: "error", label: "Canceled" },
            PAUSED: { color: "warning", label: "Paused" },
        };
        return configs[study?.status] || { color: "textSecondary", label: study?.status };
    }, [study?.status]);

    const eventInstances = study?.Event?.EventInstances || study?.Sessions?.EventInstances || [];
    const sessionCount = eventInstances.length;
    const lastTaughtString = parseTimeFrame(eventInstances[0]?.startTime, false);
    const studentCount = study?.Students?.length || 0;
    const teacherNames = [study?.Leader, study?.CoLeader]
        .filter(Boolean)
        .map(person => `${person.firstName || ''} ${person.lastName || ''}`.trim())
        .filter(Boolean)
        .join(' and ');
    const route = "/biblestudies/" + study?.id;

    const badges = [
        { label: 'Bible Study', colorVariant: 'groups', icon: 'book-outline' },
        statusConfig.label && { label: statusConfig.label, colorVariant: statusConfig.color },
    ].filter(Boolean);

    const stats = [
        { icon: 'calendar-outline', count: sessionCount, label: `Session${sessionCount !== 1 ? 's' : ''}` },
        { icon: 'people-outline', count: studentCount, label: `Student${studentCount !== 1 ? 's' : ''}` },
    ];

    return (
        <ResourceCard
            variant={variant === 'alternate' ? 'compact' : variant}
            selfSizing={variant === 'alternate'}
            flat={variant === 'alternate'}
            style={variant === 'alternate' ? styles.alternate : undefined}
            heroImage={heroImage}
            avatar={teacherAvatar}
            title={study?.name}
            subtitle={teacherNames ? `Taught by ${teacherNames}` : undefined}
            date={sessionCount > 0 ? lastTaughtString : undefined}
            badges={badges}
            stats={stats}
            moduleColor="groups"
            onPress={() => router.push(route)}
            menuOptions={[
                { name: "Log a Session", onSelect: () => router.push({ pathname: "/biblestudies/sessions/create", params: { smallGroupID: study?.id } }) },
                { name: "Edit", onSelect: () => router.push({ pathname: "/biblestudies/create", params: { id: study?.id } }) },
            ]}
            testID="BibleStudyCard"
        />
    );
}

const styles = StyleSheet.create(({ colors, spacing }) => ({
    alternate: {
        backgroundColor: colors.surfaceVariant,
        marginBottom: spacing.sm,
    },
}));
